import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button/Button";
import { Input } from "@/components/ui/input/Input";
import { useToast } from "@/lib/hooks/useToast";
import ManagementService from "@/lib/services/management/ManagementService";
import { IPIC } from "@/models/management/PIC";
import { IManagedRoom } from "@/models/management/Room";
import { MapPin, Users, Search } from "lucide-react";
import { RoomDetailDialog } from "./RoomDetailDialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination/Pagination";

export default function RoomManagementTable({ setLoading }: { setLoading: (l: boolean) => void }) {
  const [rooms, setRooms] = useState<IManagedRoom[]>([]);
  const [availablePICs, setAvailablePICs] = useState<IPIC[]>([]);
  const hasFetchedRef = useRef(false);

  const [search, setSearch] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");

  const [appliedFilter, setAppliedFilter] = useState({ search: "", locationId: "" });

  const [selectedRoom, setSelectedRoom] = useState<IManagedRoom | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { toast } = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchSupportData = async () => {
    setLoading(true);
    try {
      const [picData, roomData] = await Promise.all([
        ManagementService.getPICs(),
        ManagementService.getRooms(),
      ]);
      setAvailablePICs(picData);
      setRooms(roomData);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load data.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchSupportData();
  }, []);

  const filteredRooms = useMemo(() => {
    return rooms.filter((room) => {
      const matchesSearch = !appliedFilter.search ||
        [room.room_code, room.room_description, room.room_type?.name]
          .filter((val): val is string => Boolean(val)) // TypeScript Fix
          .some((val) => val.toLowerCase().includes(appliedFilter.search.toLowerCase()));

      const matchesLocation = !appliedFilter.locationId ||
        room.room_location?.id?.toString() === appliedFilter.locationId;

      return matchesSearch && matchesLocation;
    });
  }, [rooms, appliedFilter]);

  const totalPages = Math.ceil(filteredRooms.length / itemsPerPage);
  const currentData = filteredRooms.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const locations = useMemo(() => {
    const locationMap = new Map<number, { id: number; location: string }>();
    rooms.forEach((room) => {
      if (room.room_location?.id != null && room.room_location.location) {
        locationMap.set(room.room_location.id, room.room_location);
      }
    });
    return Array.from(locationMap.values()).sort((a, b) => a.location.localeCompare(b.location));
  }, [rooms]);

  const handleFilter = () => {
    setAppliedFilter({ search: search.trim(), locationId: selectedLocationId });
    setCurrentPage(1);
  };

  const handleOpenDetail = (roomId: number) => {
    const detail = rooms.find((r) => r.id === roomId);
    if (detail) {
      setSelectedRoom(detail);
      setIsDetailOpen(true);
    }
  };

  const handleAssignPIC = async (userId: number) => {
    if (!selectedRoom) return;
    try {
      await ManagementService.assignRoomPIC(selectedRoom.id, userId);
      const updatedRooms = await ManagementService.getRooms();
      const updatedRoom = updatedRooms.find((r: IManagedRoom) => r.id === selectedRoom.id);
      setSelectedRoom(updatedRoom || null);
      setRooms(updatedRooms);
      toast({ title: "Success", description: "PIC assigned successfully." });
    } catch (error) {
      toast({ title: "Error", description: "Assignment failed.", variant: "destructive" });
    }
  };

  const handleRemovePIC = async (userId: number) => {
    if (!selectedRoom) return;
    try {
      await ManagementService.removeRoomPIC(selectedRoom.id, userId);
      const updatedRooms = await ManagementService.getRooms();
      const updatedRoom = updatedRooms.find((r: IManagedRoom) => r.id === selectedRoom.id);
      setSelectedRoom(updatedRoom || null);
      setRooms(updatedRooms);
      toast({ title: "Success", description: "PIC removed successfully." });
    } catch (error) {
      toast({ title: "Error", description: "Removal failed.", variant: "destructive" });
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4 shadow-sm">
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="w-64 bg-background border-input h-9 pl-9"
                placeholder="Search room code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleFilter()}
              />
            </div>
            <Button variant="blue" size="sm" onClick={handleFilter} className="h-9">Filter</Button>
          </div>

          <select
            className="h-9 w-[220px] rounded-md border border-input bg-background px-3 text-sm"
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
          >
            <option value="">All Locations</option>
            {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.location}</option>)}
          </select>
        </div>

        <div className="rounded-lg border border-border bg-card shadow-md overflow-hidden">
          <table className="w-full text-left table-fixed">
            <thead className="bg-secondary/30">
              <tr className="border-b border-border">
                <th className="px-6 py-4 text-sm font-lsemibold w-[10%]">Room ID</th>
                <th className="px-6 py-4 text-sm font-lsemibold w-[40%]">Room Code</th>
                <th className="px-6 py-4 text-sm font-lsemibold w-[35%]">Location</th>
                <th className="px-6 py-4 text-sm font-lsemibold w-[15%]">Capacity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {currentData.map((room) => (
                <tr key={room.id} className="cursor-pointer hover:bg-muted/30" onClick={() => handleOpenDetail(room.id)}>
                  <td className="px-6 py-4 text-sm text-foreground/80">#{room.id}</td>
                  <td className="px-6 py-4 text-sm font-lsemibold">
                    {room.room_code} {room.is_inactive && <span className="ml-2 text-[10px] text-rose-600 bg-rose-100 px-2 py-0.5 rounded-full">Inactive</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground/80">
                    <div className="flex items-center gap-2"><MapPin size={14} />{room.room_location?.location || "-"}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground/80">
                    <div className="flex items-center gap-2"><Users size={14} />{room.room_capacity}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="p-4 flex justify-center border-t border-border">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious href="#" onClick={(e) => { e.preventDefault(); if (currentPage > 1) setCurrentPage(currentPage - 1); }} />
                </PaginationItem>
                {[...Array(totalPages)].map((_, i) => (
                  <PaginationItem key={i}>
                    <PaginationLink href="#" isActive={currentPage === i + 1} onClick={(e) => { e.preventDefault(); setCurrentPage(i + 1); }}>{i + 1}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext href="#" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) setCurrentPage(currentPage + 1); }} />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </div>

      <RoomDetailDialog
        isOpen={isDetailOpen}
        room={selectedRoom}
        availablePICs={availablePICs}
        onClose={() => { setIsDetailOpen(false); setSelectedRoom(null); }}
        onAssignPIC={handleAssignPIC}
        onRemovePIC={handleRemovePIC}
      />
    </>
  );
}
